/**
 * SCRUM-1032 — what has to exist before the Station step is worth opening.
 *
 * A station is the point where every earlier dataset meets: it needs a plant, a
 * soil, an emitter and a sub-area before it can be written, and the save
 * refuses it otherwise. Opening the step first is not harmless — it presents a
 * thousand rows of dropdowns with nothing in them, and the only way forward is
 * to leave and come back, which is exactly the wandering the wizard exists to
 * prevent.
 *
 * An area with no sub-areas does not count: stations attach to the sub-area,
 * not the area, so an area on its own leaves the Sub Area column empty.
 */

export interface StationPrerequisiteCounts {
  plants: number
  soils: number
  emitters: number
  areas: number
  /** Sub-areas across every area — what a station actually attaches to. */
  subAreas: number
}

/**
 * What a station needs, named rather than worded: the sentence the user reads
 * is assembled from translation keys by the caller, so nothing here is English.
 */
export type StationPrerequisite =
  | 'plant'
  | 'soil'
  | 'emitter'
  | 'area'
  | 'subArea'

export interface StationStepReadiness {
  ready: boolean
  /** What is still missing, in the order the wizard asks for it. */
  missing: StationPrerequisite[]
}

export const stationStepReadiness = (
  counts: Partial<StationPrerequisiteCounts> | undefined,
): StationStepReadiness => {
  const missing: StationPrerequisite[] = []
  const has = (value: number | undefined) => Number(value ?? 0) > 0

  if (!has(counts?.plants)) missing.push('plant')
  if (!has(counts?.soils)) missing.push('soil')
  if (!has(counts?.emitters)) missing.push('emitter')
  if (!has(counts?.areas)) missing.push('area')
  else if (!has(counts?.subAreas)) missing.push('subArea')

  return { ready: missing.length === 0, missing }
}

/**
 * `/api/area` answers with a FLAT list: an area and its sub-areas are siblings
 * in the same array, told apart by `parentId` (and `level`). The nested
 * `subAreas` field is on every record and empty on all of them, so counting it
 * reported zero sub-areas on a controller that plainly had some.
 */
const isSubArea = (area: any) =>
  // A top-level area reports its parent as "0", not as blank — treating any
  // non-empty string as a parent made every area look like a sub-area.
  Number(area?.parentId ?? 0) > 0 || Number(area?.level ?? 1) > 1

// `Array.isArray`, not `?? []`: some list hooks answer an empty page with
// `false` rather than an empty array, and `??` lets that through into
// `.filter`.
const asList = (areas: any): any[] => (Array.isArray(areas) ? areas : [])

/** Sub-areas in the list — what a station actually attaches to. */
export const countSubAreas = (areas: any[] | undefined) =>
  asList(areas).reduce(
    (total, area) =>
      total +
      (isSubArea(area)
        ? 1
        : // Kept for a nested answer, in case another endpoint gives one.
          (area?.subAreas?.length ?? 0)),
    0,
  )

/** Top-level areas, which is not the same as the length of the list. */
export const countAreas = (areas: any[] | undefined) =>
  asList(areas).filter((area) => !isSubArea(area)).length

/**
 * SCRUM-1032 QC round 4, item 6 — the areas that have no sub-area under them,
 * by name.
 *
 * The step used to be satisfied by one sub-area anywhere on the controller, so
 * a second area added afterwards and left empty walked straight past (Wei
 * Woon, 27 Sep: "all area must have atleast 1 sub area before proceed to next
 * step"). A station attaches to a sub-area, so an area without one is an area
 * nothing can be put in.
 *
 * Names rather than a count: the message has to say which, and on a controller
 * with a dozen areas a bare number sends the user hunting.
 */
export const areasWithoutSubArea = (areas: any[] | undefined): string[] => {
  const list = asList(areas)
  const parentIds = new Set(
    list
      .filter(isSubArea)
      .map((area) => Number(area?.parentId ?? 0))
      .filter((id) => id > 0),
  )

  return list
    .filter((area) => !isSubArea(area))
    .filter((area) => !parentIds.has(Number(area?.id)))
    .map((area, index) => String(area?.name ?? '').trim() || `#${index + 1}`)
}
